import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession, fetchUserAttributes, signIn } from 'aws-amplify/auth';

import * as Logger from '@/lib/monitoring/logger';
import { invalidateUserData } from '@/lib/query/cache-invalidation';
import type {
  LoginResult,
} from '../types/use-login-types';

interface LoginPayload {
  email: string;
  password: string;
}

async function performSignIn(email: string, password: string): Promise<LoginResult> {
  const result = await signIn({ username: email, password });

  if (!result.isSignedIn) {
    return {
      isSignedIn: result.isSignedIn,
      nextStep: { signInStep: result.nextStep.signInStep },
    };
  }

  const [session, attributes] = await Promise.all([
    fetchAuthSession(),
    fetchUserAttributes(),
  ]);

  const jwtToken = session.tokens?.idToken?.toString();

  return {
    isSignedIn: true,
    nextStep: { signInStep: result.nextStep.signInStep },
    signInUserSession: jwtToken ? { idToken: { jwtToken } } : undefined,
    attributes: {
      sub: attributes.sub,
      email: attributes.email,
      name: attributes.name,
    },
    username: attributes.sub,
  };
}

async function recoverSignInWithLowercaseEmail(
  lowercaseEmail: string,
  password: string,
  firstError: unknown
): Promise<LoginResult> {
  try {
    return await performSignIn(lowercaseEmail, password);
  } catch {
    throw firstError;
  }
}

export function useLogin() {

  const queryClient = useQueryClient();

  const { status, isSuccess, isError, isLoading, error, mutate, mutateAsync } = useMutation({
    mutationFn: async (payload: LoginPayload) => {
      const { email, password } = payload;

      if (!email.trim() || !password) {
        throw Object.assign(new Error('Email and password are required'), {
          name: 'InvalidParameterException',
        });
      }

      try {
        return await performSignIn(email, password);
      } catch (firstError: any) {
        if (
          firstError.name === 'UserNotFoundException' ||
          firstError.name === 'NotAuthorizedException'
        ) {
          const lowercaseEmail = email.toLowerCase();
          if (lowercaseEmail !== email) {
            return recoverSignInWithLowercaseEmail(lowercaseEmail, password, firstError);
          }
        }
        throw firstError;
      }
    },
    onSuccess: res => {
      Logger.info('Login request successfully', {
        functionName: 'useLogin',
        isSignedIn: res.isSignedIn,
        signInStep: res.nextStep.signInStep,
      });
      if (res.isSignedIn) {
        invalidateUserData(queryClient);
      }
    },
    onError: (error: unknown) => {
      Logger.error(
        'Login request failed',
        error instanceof Error ? error : null,
        { functionName: 'useLogin' }
      );
    },
  });

  return {
    status,
    isSuccess,
    isError,
    isLoading,
    error: error,
    mutate,
    mutateAsync,
  };
}
